import {
  IAddress,
  ICommitteeMemberInput,
  ICooperativeDetails,
  IDeclaration,
  IDocumentsInput,
  IExecutiveMemberInput,
  IFinancialAuditInput,
  IFinancialIncome,
  IFinancialLoan,
  IFinancialShare,
  IFinancialTax,
  IShareMemberInput,
} from "../../exports/interfaces";

export const isValidEmail = (email: string): boolean => {
  const normalizedEmail = email.trim();

  const emailRegex =
    /^[a-zA-Z0-9.!#$%&'*+/=?^_`{|}~-]+@[a-zA-Z0-9](?:[a-zA-Z0-9-]{0,61}[a-zA-Z0-9])?(?:\.[a-zA-Z0-9](?:[a-zA-Z0-9-]{0,61}[a-zA-Z0-9])?)*$/;

  if (!emailRegex.test(normalizedEmail)) {
    return false;
  }

  if (normalizedEmail.length > 254) {
    return false;
  }

  const [localPart, domain] = normalizedEmail.split("@");

  if (localPart.length > 64) {
    return false;
  }

  const domainParts = domain.split(".");

  if (domainParts.length < 2) {
    return false;
  }

  const allowedTlds = new Set([
    "com",
    "org",
    "net",
    "edu",
    "gov",
    "mil",
    "int",
    "info",
    "biz",
    "io",
    "co",
    "me",
    "app",
    "dev",
    "ai",
    "in",
    "np",
    "com.np",
    "org.np",
    "edu.np",
    "gov.np",
    "ac.np",
  ]);

  const tld = domainParts.slice(-2).join(".").toLowerCase();
  const lastTld = domainParts[domainParts.length - 1].toLowerCase();

  if (!/^[a-z]{2,63}$/.test(lastTld)) {
    return false;
  }

  if (!allowedTlds.has(lastTld) && !allowedTlds.has(tld)) {
    return false;
  }

  return true;
};

export const isValidPhone = (phone: string) => /^\d{10}$/.test(phone);
export const isValidUDIN = (value: string) => /^\d{18}$/.test(value);

export const getCooperativeInfoErrors = (data: ICooperativeDetails) => {
  const validationErrors: Record<string, string> = {};

  if (!data.registrationNumber?.trim()) {
    validationErrors.registrationNumber = "Registration Number is required.";
  }

  if (!data.businessregistrationNumber?.trim()) {
    validationErrors.businessregistrationNumber =
      "Business Registration Number is required.";
  }

  if (!data.nameNepali?.trim()) {
    validationErrors.nameNepali = "Cooperative Name in Nepali is required.";
  }

  if (!data.nameEnglish?.trim()) {
    validationErrors.nameEnglish = "Cooperative Name in English is required.";
  }

  if (!data.natureOfWork?.trim()) {
    validationErrors.natureOfWork = "Nature of Work is required.";
  }

  if (!data.status?.trim()) {
    validationErrors.status = "Cooperative Status is required.";
  }

  if (data.status?.trim().toLowerCase() === "merged") {
    if (!data.mergedCooperativeName?.trim()) {
      validationErrors.mergedCooperativeName =
        "Name of Cooperative Merged is required.";
    }

    if (!data.mergedDateNepali?.trim()) {
      validationErrors.mergedDateNepali = "Merged Date is required.";
    }

    if (!data.mergedDateEnglish?.trim()) {
      validationErrors.mergedDateEnglish = "Merged English Date is required.";
    }
  }

  if (!data.workingArea?.length) {
    validationErrors.workingArea = "Working Area is required.";
  }

  if (!data.panNumber?.trim()) {
    validationErrors.panNumber = "PAN Number is required.";
  } else if (!/^\d{9}$/.test(data.panNumber.trim())) {
    validationErrors.panNumber = "Exactly 9 digits required.";
  }

  if (!data.contactPerson?.trim()) {
    validationErrors.contactPerson = "Administrator's Name is required.";
  }

  if (!data.mobileNumber?.trim()) {
    validationErrors.mobileNumber =
      "Administrator's Mobile Number is required.";
  } else if (!isValidPhone(data.mobileNumber)) {
    validationErrors.mobileNumber = "Exactly 10 digits required.";
  }

  if (!data.email?.trim()) {
    validationErrors.email = "Administrator's Email is required.";
  } else if (!isValidEmail(data.email)) {
    validationErrors.email = "Invalid Email format.";
  }

  if (!data.registeredDate?.trim()) {
    validationErrors.registeredDate = "Registered Date is required.";
  }

  if (!data.registeredEnglishDate?.trim()) {
    validationErrors.registeredEnglishDate =
      "Registered English Date is required.";
  }

  if (!data.registeredAt?.trim()) {
    validationErrors.registeredAt = "Registered At is required.";
  }

  if (!data.registeredOfficeAddress?.trim()) {
    validationErrors.registeredOfficeAddress =
      "Registered Office Address is required.";
  }

  if (data.maleMembers === null || data.maleMembers === undefined) {
    validationErrors.maleMembers = "Male Members is required.";
  }

  if (data.femaleMembers === null || data.femaleMembers === undefined) {
    validationErrors.femaleMembers = "Female Members is required.";
  }

  if (data.otherMembers === null || data.otherMembers === undefined) {
    validationErrors.otherMembers = "Other Members is required.";
  }

  if (data.staffCount === null || data.staffCount === undefined) {
    validationErrors.staffCount = "Current Working Staff is required.";
  }

  if (!data.softwareUsed?.trim()) {
    validationErrors.softwareUsed = "Software Used is required.";
  }

  if (
    data.isRegisteredInFederation === null ||
    data.isRegisteredInFederation === undefined ||
    data.isRegisteredInFederation === "null"
  ) {
    validationErrors.isRegisteredInFederation = "Please filed is required.";
  }

  if (
    data.womenLead === null ||
    data.womenLead === undefined ||
    data.womenLead === "null"
  ) {
    validationErrors.womenLead = "Women Lead is required.";
  }

  if (data.status?.toLowerCase() === "deactivate") {
    if (!data.closeDateNepali?.trim()) {
      validationErrors.closeDateNepali = "Closed Date is required.";
    }

    if (!data.closeDateEnglish?.trim()) {
      validationErrors.closeDateEnglish = "Closed English Date is required.";
    }
  }

  return validationErrors;
};

export const cooperativeInfoFieldOrder = [
  "registrationNumber",
  "businessregistrationNumber",
  "nameNepali",
  "nameEnglish",
  "natureOfWork",
  "status",
  "mergedCooperativeName",
  "mergedDateNepali",
  "mergedDateEnglish",
  "workingArea",
  "panNumber",
  "contactPerson",
  "mobileNumber",
  "email",
  "registeredDate",
  "registeredEnglishDate",
  "registeredAt",
  "registeredOfficeAddress",
  "maleMembers",
  "femaleMembers",
  "otherMembers",
  "staffCount",
  "softwareUsed",
  "isRegisteredInFederation",
  "womenLead",
  "closeDateNepali",
  "closeDateEnglish",
];

export const getAddressErrors = (data: IAddress) => {
  const validationErrors: Record<string, string> = {};

  if (!data.province?.trim()) {
    validationErrors.province = "Province is required.";
  }

  if (!data.district?.trim()) {
    validationErrors.district = "District is required.";
  }

  if (!data.municipality?.trim()) {
    validationErrors.municipality = "Municipality is required.";
  }

  if (!data.streetTole?.trim()) {
    validationErrors.streetTole = "Street / Tole is required.";
  }

  if (!data.ward?.trim()) {
    validationErrors.ward = "Ward is required.";
  }

  return validationErrors;
};

export const addressFieldOrder = [
  "province",
  "district",
  "municipality",
  "streetTole",
  "ward",
];

export const getShareFinancialErrors = (data: IFinancialShare) => {
  const validationErrors: Record<string, string> = {};

  if (!data.fiscalYear?.trim()) {
    validationErrors.fiscalYear = "Fiscal Year is required.";
  }

  if (data.maleMembers === null || data.maleMembers === undefined) {
    validationErrors.maleMembers = "Male Members is required.";
  }

  if (data.femaleMembers === null || data.femaleMembers === undefined) {
    validationErrors.femaleMembers = "Female Members is required.";
  }

  if (data.otherMembers === null || data.otherMembers === undefined) {
    validationErrors.otherMembers = "Other Members is required.";
  }

  if (
    data.institutionalMembers === null ||
    data.institutionalMembers === undefined
  ) {
    validationErrors.institutionalMembers =
      "Institutional Members is required.";
  }

  if (data.totalShareMembers === null || data.totalShareMembers === undefined) {
    validationErrors.totalShareMembers = "Total Share Members is required.";
  }

  if (!data.totalGeneralSaving?.toString().trim()) {
    validationErrors.totalGeneralSaving = "Total General Saving is required.";
  }

  if (!data.totalRegularSaving?.toString().trim()) {
    validationErrors.totalRegularSaving = "Total Regular Saving is required.";
  }

  if (!data.totalPeriodicSaving?.toString().trim()) {
    validationErrors.totalPeriodicSaving = "Total Periodic Saving is required.";
  }

  if (!data.totalSavingAmount?.toString().trim()) {
    validationErrors.totalSavingAmount = "Total Saving Amount is required.";
  }

  if (!data.femaleDepositors?.toString().trim()) {
    validationErrors.femaleDepositors = "Female Depositors is required.";
  }

  if (!data.maleDepositors?.toString().trim()) {
    validationErrors.maleDepositors = "Male Depositors is required.";
  }

  if (!data.otherDepositors?.toString().trim()) {
    validationErrors.otherDepositors = "Other Depositors is required.";
  }

  if (!data.totalDepositors?.toString().trim()) {
    validationErrors.totalDepositors = "Total Depositors is required.";
  }

  if (!data.totalLiabilities?.toString().trim()) {
    validationErrors.totalLiabilities = "Total Liabilities is required.";
  }

  if (!data.totalAssets?.toString().trim()) {
    validationErrors.totalAssets = "Total Assets is required.";
  }

  if (!data.totalLiquidAssets?.toString().trim()) {
    validationErrors.totalLiquidAssets = "Total Liquid Assets is required.";
  }

  if (!data.balanceSheet?.toString().trim()) {
    validationErrors.balanceSheet = "Balance Sheet is required.";
  }

  if (!data.tlaToTaRatio?.toString().trim()) {
    validationErrors.tlaToTaRatio = "TLA to TA ratio is required.";
  }

  if (!data.cash?.toString().trim()) {
    validationErrors.cash = "Cash is required.";
  }

  if (!data.association?.trim()) {
    validationErrors.association = "Association is required.";
  }

  if (!data.districtUnion?.trim()) {
    validationErrors.districtUnion = "District Union is required.";
  }

  if (!data.bank?.trim()) {
    validationErrors.bank = "Bank / Cooperative is required.";
  }

  return validationErrors;
};

export const getShareFinancialTab = (errors: Record<string, string>) => {
  if (
    errors.fiscalYear ||
    errors.maleMembers ||
    errors.femaleMembers ||
    errors.otherMembers ||
    errors.institutionalMembers ||
    errors.totalShareMembers ||
    errors.totalGeneralSaving ||
    errors.totalRegularSaving ||
    errors.totalPeriodicSaving ||
    errors.totalSavingAmount ||
    errors.femaleDepositors ||
    errors.maleDepositors ||
    errors.otherDepositors ||
    errors.totalDepositors ||
    errors.totalLiabilities ||
    errors.totalAssets ||
    errors.totalLiquidAssets ||
    errors.balanceSheet ||
    errors.tlaToTaRatio ||
    errors.cash ||
    errors.association ||
    errors.districtUnion ||
    errors.bank
  ) {
    return "share";
  }

  return "share";
};

export const getIncomeFinancialErrors = (data: IFinancialIncome) => {
  const validationErrors: Record<string, string> = {};
  const requiredFields: Array<[keyof IFinancialIncome, string]> = [
    ["fiscalYear", "Fiscal Year is required."],
    ["operationalIncome", "Operational Income is required."],
    ["otherIncome", "Other Income is required."],
    ["operationalExpenses", "Operational Expenses is required."],
    ["otherExpenses", "Other Expenses is required."],
    ["netProfitLoss", "Net Profit / Loss is required."],
  ];

  requiredFields.forEach(([field, message]) => {
    const value = data[field];
    if (value === null || value === undefined || value === "") {
      validationErrors[field as string] = message;
    }
  });

  return validationErrors;
};

export const getIncomeFinancialTab = (errors: Record<string, string>) => {
  if (Object.keys(errors).length > 0) {
    return "income";
  }

  return "income";
};

export const getAuditFinancialErrors = (data: IFinancialAuditInput) => {
  const validationErrors: Record<string, string> = {};

  if (!data.fiscalYear?.trim()) {
    validationErrors.fiscalYear = "Fiscal Year is required.";
  }

  if (!data.auditorName?.trim()) {
    validationErrors.auditorName = "Auditor's Name is required.";
  }

  if (!data.auditorMobile?.trim()) {
    validationErrors.auditorMobile = "Auditor's Mobile Number is required.";
  } else if (!isValidPhone(data.auditorMobile)) {
    validationErrors.auditorMobile = "Exactly 10 digits required.";
  }

  if (!data.auditorAddress?.trim()) {
    validationErrors.auditorAddress = "Auditor's Address is required.";
  }

  if (!data.icanNumber?.trim()) {
    validationErrors.icanNumber = "Auditer’s Membership Number is required.";
  }
  // else if (!isValidUDIN(data.icanNumber)) {
  //   validationErrors.icanNumber = "Exactly 18 digits required.";
  // }

  if (!data.agmDate?.trim()) {
    validationErrors.agmDate = "AGM English Date is required.";
  }

  if (!data.agmNepaliDate?.trim()) {
    validationErrors.agmNepaliDate = "AGM Date is required.";
  }

  if (data.participants === null || data.participants === undefined) {
    validationErrors.participants = "Participants Invited is required.";
  }

  if (
    data.participantsAttended === null ||
    data.participantsAttended === undefined
  ) {
    validationErrors.participantsAttended =
      "Participants Attended is required.";
  }

  if (!data.participantsPercent?.toString().trim()) {
    validationErrors.participantsPercent =
      "Participants Percentage is required.";
  }

  if (!data.file && !data.documentUrl) {
    validationErrors.document = "Audit Document is required.";
  }

  return validationErrors;
};

export const getAuditFinancialTab = (errors: Record<string, string>) => {
  if (Object.keys(errors).length > 0) {
    return "audit";
  }

  return "audit";
};

export const getTaxFinancialErrors = (data: IFinancialTax) => {
  const validationErrors: Record<string, string> = {};

  if (!data.fiscalYear?.trim()) {
    validationErrors.fiscalYear = "Fiscal Year is required.";
  }

  if (!data.incomeTax?.toString().trim()) {
    validationErrors.incomeTax = "Income Tax is required.";
  }

  if (!data.interestTax?.toString().trim()) {
    validationErrors.interestTax = "Interest Tax is required.";
  }

  if (!data.employeeTax?.toString().trim()) {
    validationErrors.employeeTax = "Employee Tax is required.";
  }

  if (!data.taxOthers?.toString().trim()) {
    validationErrors.taxOthers = "Other Tax is required.";
  }

  if (!data.serviceTax?.toString().trim()) {
    validationErrors.serviceTax = "Service Tax is required.";
  }

  if (!data.rentalTax?.toString().trim()) {
    validationErrors.rentalTax = "Rental Tax is required.";
  }

  if (!data.localOthers?.toString().trim()) {
    validationErrors.localOthers = "Other tax is required.";
  }

  return validationErrors;
};

export const getTaxFinancialTab = (errors: Record<string, string>) => {
  if (Object.keys(errors).length > 0) {
    return "tax";
  }

  return "tax";
};

export const getLoanFinancialErrors = (data: IFinancialLoan) => {
  const validationErrors: Record<string, string> = {};
  const requiredFields: Array<[keyof IFinancialLoan, string]> = [
    ["fiscalYear", "Fiscal Year is required."],
    ["totalShareCapital", "Total Share Capital is required."],
    ["totalReserveFund", "Total Reserve Fund is required."],
    ["deficitReplenishmentFund", "Deficit Replenishment Fund is required."],
    ["accumulatedProfitLoss", "Accumulated Profit / Loss is required."],
    ["coreCapital", "Core Capital is required."],
    ["totalPassLoan", "Total Pass Loan is required."],
    ["totalSubStandardLoan", "Total Sub-Standard Loan is required."],
    ["totalDoubtfulLoan", "Total Doubtful Loan is required."],
    ["totalLossLoan", "Total Loss Loan is required."],
    ["totalLoanDisbursed", "Total Loan Disbursed is required."],
    ["externalLoan", "External Loan is required."],
    ["maleBorrowers", "Male Borrowers is required."],
    ["femaleBorrowers", "Female Borrowers is required."],
    ["othersBorrowers", "Others Borrowers is required."],
    ["totalBorrowers", "Total Borrowers is required."],
    ["llpPassLoan", "LLP for Pass Loan is required."],
    ["llpSubStandardLoan", "LLP for Sub-Standard Loan is required."],
    ["llpDoubtfulLoan", "LLP for Doubtful Loan is required."],
    ["llpLossLoan", "LLP for Loss Loan is required."],
    ["totalLoanLossProvision", "Total Loan Loss Provision is required."],
    [
      "additionalProvisionLoanLoss",
      "Additional Provision of Loan Loss is required.",
    ],
    ["assetsRevaluationReserve", "Assets Revaluation Reserve is required."],
    ["freeReserve", "Free Reserve is required."],
    ["supplementaryCapital", "Supplementary Capital is required."],
    ["totalCapitalFund", "Total Capital Fund is required."],
    ["totalNonPerformingLoan", "Total Non-Performing Loan is required."],
    ["nplPercentage", "NPL Percentage is required."],
    ["totalRiskWeightedAssets", "Total Risk Weighted Assets is required."],
    ["coreCapitalRatio", "Core Capital Ratio is required."],
    ["totalCapitalFundRatio", "Total Capital Fund Ratio is required."],
    ["totalBorrowing", "Total Borrowing is required."],
    ["loanRescheduledCount", "Loan Rescheduled is required."],
    ["loanRescheduledAmount", "Loan Rescheduled Amount is required."],
    ["investmentPrivateLtd", "Investment in Pvt. Ltd is required."],
    ["investmentHouseBuilding", "Investment in House / Building is required."],
  ];

  requiredFields.forEach(([field, message]) => {
    const value = data[field];
    if (value === null || value === undefined || value === "") {
      validationErrors[field as string] = message;
    }
  });

  return validationErrors;
};

export const getLoanFinancialTab = (errors: Record<string, string>) => {
  if (Object.keys(errors).length > 0) {
    return "loan";
  }

  return "loan";
};

export const getCommitteeMemberErrors = (data: ICommitteeMemberInput) => {
  const validationErrors: Record<string, string> = {};

  if (!data.committeeType?.trim()) {
    validationErrors.committeeType = "Committee Type is required.";
  }

  if (!data.position?.trim()) {
    validationErrors.position = "Position is required.";
  }

  if (!data.positionStatus?.trim()) {
    validationErrors.positionStatus = "Position Status is required.";
  }

  if (!data.fullName?.trim()) {
    validationErrors.fullName = "Full Name is required.";
  }

  if (!data.gender?.trim()) {
    validationErrors.gender = "Gender is required.";
  }

  if (!data.mobile?.trim()) {
    validationErrors.mobile = "Mobile number is required.";
  } else if (!isValidPhone(data.mobile)) {
    validationErrors.mobile = "Exactly 10 digits required.";
  }

  if (!data.grandmotherName?.trim()) {
    validationErrors.grandmotherName =
      "Grandfather / Grandmother Name is required.";
  }

  if (!data.fatherName?.trim()) {
    validationErrors.fatherName = "Father / Mother Name is required.";
  }

  if (!data.maritalStatus?.trim()) {
    validationErrors.maritalStatus = "Marital Status is required.";
  }

  if (data.maritalStatus === "Married" && !data.husbandName?.trim()) {
    validationErrors.husbandName = "Husband / Wife Name is required.";
  }

  if (!data.email?.trim()) {
    validationErrors.email = "Email is required.";
  } else if (!isValidEmail(data.email)) {
    validationErrors.email = "Invalid Email address.";
  }

  if (!data.address?.trim()) {
    validationErrors.address = "Address is required.";
  }

  if (!data.citizenship?.trim()) {
    validationErrors.citizenship = "Citizenship Number is required.";
  } else if (!/^\d{14}$/.test(data.citizenship.trim())) {
    validationErrors.citizenship = "Exactly 14 digits Required.";
  }

  if (!data.pan?.trim()) {
    validationErrors.pan = "PAN Number is required.";
  } else if (!/^\d{9}$/.test(data.pan.trim())) {
    validationErrors.pan = "Exactly 9 digits Required.";
  }

  if (!data.nid?.trim()) {
    validationErrors.nid = "NID is required.";
  } else if (!/^\d{10}$/.test(data.nid.trim())) {
    validationErrors.nid = "Exactly 10 digits Required.";
  }

  if (!data.issuedDistrict?.trim()) {
    validationErrors.issuedDistrict = "Issued District is required.";
  }

  if (!data.tenureStart?.trim()) {
    validationErrors.tenureStart = "Tenure Start Date is required.";
  }

  if (!data.tenureEnd?.trim()) {
    validationErrors.tenureEnd = "Tenure End Date is required.";
  }

  if (!data.province?.trim()) {
    validationErrors.province = "Province is required.";
  }

  if (!data.district?.trim()) {
    validationErrors.district = "District is required.";
  }

  if (!data.municipality?.trim()) {
    validationErrors.municipality = "Municipality is required.";
  }

  if (!data.ward?.trim()) {
    validationErrors.ward = "Ward is required.";
  }

  if (!data.street?.trim()) {
    validationErrors.street = "Street is required.";
  }

  if (!data.education?.trim()) {
    validationErrors.education = "Education is required.";
  }

  if (!data.file && !data.documentUrl) {
    validationErrors.file = "Photo is required.";
  }

  // if (!data.bodFile && !data.bodDocumentUrl) {
  //   validationErrors.bodFile = "BOD Document is required.";
  // }

  if (!data.citizenshipFront && !data.citizenshipFrontUrl) {
    validationErrors.citizenshipFront = "Citizenship front image is required.";
  }

  if (!data.citizenshipBack && !data.citizenshipBackUrl) {
    validationErrors.citizenshipBack = "Citizenship back image is required.";
  }

  return validationErrors;
};

export const getExecutiveMemberErrors = (data: IExecutiveMemberInput) => {
  const validationErrors: Record<string, string> = {};

  if (!data.position?.trim()) {
    validationErrors.position = "Position is required.";
  }

  if (!data.positionStatus?.trim()) {
    validationErrors.positionStatus = "Position Status is required.";
  }

  if (!data.grandmotherName?.trim()) {
    validationErrors.grandmotherName =
      "Grandfather / Grandmother Name is required.";
  }

  if (!data.fatherName?.trim()) {
    validationErrors.fatherName = "Father / Mother Name is required.";
  }

  if (!data.maritalStatus?.trim()) {
    validationErrors.maritalStatus = "Marital Status is required.";
  }

  if (data.maritalStatus === "Married" && !data.husbandName?.trim()) {
    validationErrors.husbandName = "Husband / Wife Name is required.";
  }

  if (!data.fullName?.trim()) {
    validationErrors.fullName = "Full Name is required.";
  }

  if (!data.gender?.trim()) {
    validationErrors.gender = "Gender is required.";
  }

  if (!data.email?.trim()) {
    validationErrors.email = "Email is required.";
  } else if (!isValidEmail(data.email)) {
    validationErrors.email = "Invalid Email address.";
  }

  if (!data.address?.trim()) {
    validationErrors.address = "Address is required.";
  }

  if (!data.mobile?.trim()) {
    validationErrors.mobile = "Mobile number is required.";
  } else if (!isValidPhone(data.mobile)) {
    validationErrors.mobile = "Exactly 10 digits required.";
  }

  if (!data.citizenship?.trim()) {
    validationErrors.citizenship = "Citizenship Number is required.";
  } else if (!/^\d{14}$/.test(data.citizenship.trim())) {
    validationErrors.citizenship = "Exactly 14 digits Required.";
  }

  if (!data.pan?.trim()) {
    validationErrors.pan = "PAN Number is required.";
  } else if (!/^\d{9}$/.test(data.pan)) {
    validationErrors.pan = "Exactly 9 digits required.";
  }

  if (!data.nid?.trim()) {
    validationErrors.nid = "NID is required.";
  } else if (!/^\d{10}$/.test(data.nid.trim())) {
    validationErrors.nid = "Exactly 10 digits Required.";
  }

  if (!data.issuedDistrict?.trim()) {
    validationErrors.issuedDistrict = "Issued District is required.";
  }

  if (!data.province?.trim()) {
    validationErrors.province = "Province is required.";
  }

  if (!data.district?.trim()) {
    validationErrors.district = "District is required.";
  }

  if (!data.municipality?.trim()) {
    validationErrors.municipality = "Municipality is required.";
  }

  if (!data.ward?.trim()) {
    validationErrors.ward = "Ward is required.";
  }

  if (!data.street?.trim()) {
    validationErrors.street = "Street is required.";
  }

  if (data.experience === null || data.experience === undefined) {
    validationErrors.experience = "Years of Experience is required.";
  }

  if (
    data.currentPositionDuration === null ||
    data.currentPositionDuration === undefined
  ) {
    validationErrors.currentPositionDuration =
      "Current Position Duration is required.";
  }

  if (!data.education?.trim()) {
    validationErrors.education = "Education is required.";
  }

  if (!data.file && !data.documentUrl) {
    validationErrors.file = "Photo is required.";
  }

  if (!data.citizenshipFront && !data.citizenshipFrontUrl) {
    validationErrors.citizenshipFront = "Citizenship front image is required.";
  }

  if (!data.citizenshipBack && !data.citizenshipBackUrl) {
    validationErrors.citizenshipBack = "Citizenship back image is required.";
  }

  return validationErrors;
};

export const getShareMemberErrors = (data: IShareMemberInput) => {
  const validationErrors: Record<string, string> = {};

  // Share Member
  if (!data.shareMemberFile && !data.shareMemberDocumentUrl) {
    validationErrors.shareMemberFile = "Share Member File is required.";
  }

  if (data.shareMaleMembers === null || data.shareMaleMembers === undefined) {
    validationErrors.shareMaleMembers = "Male Share Members is required.";
  }

  if (
    data.shareFemaleMembers === null ||
    data.shareFemaleMembers === undefined
  ) {
    validationErrors.shareFemaleMembers = "Female Share Members is required.";
  }

  if (
    data.shareOthersMembers === null ||
    data.shareOthersMembers === undefined
  ) {
    validationErrors.shareOthersMembers = "Other Share Members is required.";
  }

  if (data.shareTotalMembers === null || data.shareTotalMembers === undefined) {
    validationErrors.shareTotalMembers = "Total Share Members is required.";
  }

  // Saving Member
  if (!data.savingMemberFile && !data.savingMemberDocumentUrl) {
    validationErrors.savingMemberFile = "Saving Member File is required.";
  }

  if (data.savingMaleMembers === null || data.savingMaleMembers === undefined) {
    validationErrors.savingMaleMembers = "Male Saving Members is required.";
  }

  if (
    data.savingFemaleMembers === null ||
    data.savingFemaleMembers === undefined
  ) {
    validationErrors.savingFemaleMembers = "Female Saving Members is required.";
  }

  if (
    data.savingOthersMembers === null ||
    data.savingOthersMembers === undefined
  ) {
    validationErrors.savingOthersMembers = "Other Saving Members is required.";
  }

  if (
    data.savingTotalMembers === null ||
    data.savingTotalMembers === undefined
  ) {
    validationErrors.savingTotalMembers = "Total Saving Members is required.";
  }

  // Loan Member
  if (!data.loanMemberFile && !data.loanMemberDocumentUrl) {
    validationErrors.loanMemberFile = "Loan Member File is required.";
  }

  if (data.loanMaleMembers === null || data.loanMaleMembers === undefined) {
    validationErrors.loanMaleMembers = "Male Loan Members is required.";
  }

  if (data.loanFemaleMembers === null || data.loanFemaleMembers === undefined) {
    validationErrors.loanFemaleMembers = "Female Loan Members is required.";
  }

  if (data.loanOthersMembers === null || data.loanOthersMembers === undefined) {
    validationErrors.loanOthersMembers = "Other Loan Members is required.";
  }

  if (data.loanTotalMembers === null || data.loanTotalMembers === undefined) {
    validationErrors.loanTotalMembers = "Total Loan Members is required.";
  }

  return validationErrors;
};

export const getDocumentErrors = (data: IDocumentsInput) => {
  const validationErrors: Record<string, string> = {};

  if (!data.regCertificateFile && !data.regCertificateDocumentUrl) {
    validationErrors.regCertificateFile =
      "Registration Certificate is required.";
  }

  if (!data.lawDocumentFile && !data.lawDocumentDocumentUrl) {
    validationErrors.lawDocumentFile = "Law Document is required.";
  }

  if (!data.foundingDocumentFile && !data.foundingDocumentDocumentUrl) {
    validationErrors.foundingDocumentFile = "Founding Document is required.";
  }

  if (!data.panDocumentFile && !data.panDocumentDocumentUrl) {
    validationErrors.panDocumentFile = "PAN Document is required.";
  }

  if (!data.meetingDocumentFile && !data.meetingDocumentDocumentUrl) {
    validationErrors.meetingDocumentFile = "Meeting Document is required.";
  }

  if (!data.businessRegistrationFile && !data.businessRegistrationDocumentUrl) {
    validationErrors.businessRegistrationFile =
      "Business Registration Document is required.";
  }

  if (!data.taxClearanceFile && !data.taxClearanceDocumentUrl) {
    validationErrors.taxClearanceFile = "Tax Clearance Document is required.";
  }

  if (!data.agmDocumentFile && !data.agmDocumentDocumentUrl) {
    validationErrors.agmDocumentFile = "AGM Document is required.";
  }

  // Uncomment if audit document is required
  // if (!data.auditDocumentFile && !data.auditDocumentDocumentUrl) {
  //   validationErrors.auditDocumentFile =
  //     "Audit document is required.";
  // }

  const hasOtherDocument =
    (data.otherDocumentFiles && data.otherDocumentFiles.length > 0) ||
    (data.otherDocumentDocumentUrls &&
      data.otherDocumentDocumentUrls.length > 0) ||
    Boolean(data.otherDocumentFile) ||
    Boolean(data.otherDocumentDocumentUrl);

  if (!hasOtherDocument) {
    validationErrors.otherDocumentFile = "Other Document is required.";
  }

  return validationErrors;
};

export const getDeclarationErrors = (data: IDeclaration) => {
  const validationErrors: Record<string, string> = {};

  if (!data.name.trim()) {
    validationErrors.name = "Name is required.";
  }

  if (!data.position.trim()) {
    validationErrors.position = "Position is required.";
  }

  if (!data.mobile.trim()) {
    validationErrors.mobile = "Mobile Number is required.";
  } else if (!isValidPhone(data.mobile)) {
    validationErrors.mobile = "Exactly 10 digits required.";
  }

  if (data.declaration !== "true") {
    validationErrors.declaration =
      "You must accept the declaration before proceeding.";
  }

  return validationErrors;
};
